import gsap from "gsap";
import type { ScrollController } from "@/app/scroll";

const SECTION_TARGETS = ["about", "projects", "contacts"] as const;
const FADE_DURATION = 0.8;
const SCROLL_DURATION = 1.4;
const REVEAL_DELAY = 0.5;
const RETURN_REVEAL_DURATION = 0.5;
const SCROLL_TIMEOUT_MS = 2_400;

type SectionTarget = (typeof SECTION_TARGETS)[number];

interface SectionNavigationOptions {
  root: HTMLElement;
  outlet: HTMLElement;
  scroll: ScrollController;
  isHome: () => boolean;
  ensureHome: () => Promise<void>;
}

function isSectionTarget(value: string | undefined): value is SectionTarget {
  return value !== undefined && SECTION_TARGETS.some((target) => target === value);
}

export class SectionNavigationController {
  readonly #root: HTMLElement;
  readonly #outlet: HTMLElement;
  readonly #scroll: ScrollController;
  readonly #isHome: () => boolean;
  readonly #ensureHome: () => Promise<void>;
  readonly #navigation?: HTMLElement;
  #timeline?: gsap.core.Timeline;
  #sequence = 0;
  #busy = false;
  #destroyed = false;

  constructor({ root, outlet, scroll, isHome, ensureHome }: SectionNavigationOptions) {
    this.#root = root;
    this.#outlet = outlet;
    this.#scroll = scroll;
    this.#isHome = isHome;
    this.#ensureHome = ensureHome;
    this.#navigation = root.querySelector<HTMLElement>(".site-header__navigation") ?? undefined;
  }

  mount(): void {
    this.#discardSectionHash();
    this.#root.addEventListener("click", this.#handleClick);
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#sequence += 1;
    this.#root.removeEventListener("click", this.#handleClick);
    this.#timeline?.kill();
    this.#timeline = undefined;
    this.#setBusy(false);
    gsap.set(this.#outlet, { clearProps: "opacity,visibility" });
  }

  readonly #handleClick = (event: MouseEvent): void => {
    if (event.defaultPrevented) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest<HTMLButtonElement>("button[data-section-target]");
    if (!trigger || !this.#root.contains(trigger) || trigger.disabled) return;
    const section = trigger.dataset.sectionTarget;
    if (!isSectionTarget(section)) return;
    event.preventDefault();
    void this.#navigate(section);
  };

  async #navigate(section: SectionTarget): Promise<void> {
    if (this.#busy || this.#destroyed) return;
    const sequence = ++this.#sequence;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const startedOnHome = this.#isHome();
    this.#setBusy(true);

    try {
      if (reducedMotion) {
        if (!startedOnHome) await this.#ensureHome();
        if (!this.#isCurrent(sequence)) return;
        const target = document.getElementById(section);
        if (target) this.#scroll.scrollTo(target, { immediate: true, force: true });
        return;
      }

      await this.#fadeTo(0);
      if (!this.#isCurrent(sequence)) return;
      if (!startedOnHome) await this.#ensureHome();
      if (!this.#isCurrent(sequence)) return;
      const target = document.getElementById(section);
      if (!target) return;
      if (!startedOnHome) {
        this.#scroll.resize();
        this.#scroll.scrollTo(target, { immediate: true, force: true });
        await this.#fadeTo(1, 0, RETURN_REVEAL_DURATION);
        return;
      }
      const scroll = this.#scrollTo(target);
      const fade = this.#fadeTo(1, REVEAL_DELAY);
      await Promise.all([fade, scroll]);
    } catch (error) {
      console.error(`Could not navigate to the ${section} section.`, error);
    } finally {
      if (this.#isCurrent(sequence)) {
        this.#timeline?.kill();
        this.#timeline = undefined;
        gsap.set(this.#outlet, { clearProps: "opacity,visibility" });
        this.#setBusy(false);
      }
    }
  }

  #fadeTo(autoAlpha: 0 | 1, position = 0, duration = FADE_DURATION): Promise<void> {
    return this.#animate((timeline) => {
      timeline.to(
        this.#outlet,
        {
          autoAlpha,
          duration,
          ease: autoAlpha === 0 ? "power2.inOut" : "power2.out",
          overwrite: "auto",
        },
        position
      );
    });
  }

  #animate(build: (timeline: gsap.core.Timeline) => void): Promise<void> {
    this.#timeline?.kill();
    let timeline: gsap.core.Timeline;
    return new Promise((resolve) => {
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        if (this.#timeline === timeline) this.#timeline = undefined;
        resolve();
      };
      timeline = gsap.timeline({ onComplete: settle, onInterrupt: settle });
      this.#timeline = timeline;
      build(timeline);
    });
  }

  #scrollTo(target: HTMLElement): Promise<void> {
    this.#scroll.resize();
    return new Promise((resolve) => {
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve();
      };
      const timeout = window.setTimeout(settle, SCROLL_TIMEOUT_MS);
      this.#scroll.scrollTo(target, {
        duration: SCROLL_DURATION,
        lock: true,
        force: true,
        onComplete: settle,
      });
    });
  }

  #setBusy(busy: boolean): void {
    this.#busy = busy;
    this.#root.classList.toggle("is-section-navigating", busy);
    this.#navigation?.setAttribute("aria-busy", String(busy));
    this.#root.querySelectorAll<HTMLButtonElement>("button[data-section-target]").forEach((button) => {
      button.disabled = busy;
    });
  }

  #isCurrent(sequence: number): boolean {
    return !this.#destroyed && sequence === this.#sequence;
  }

  #discardSectionHash(): void {
    if (!SECTION_TARGETS.some((target) => window.location.hash === `#${target}`)) return;
    history.replaceState(history.state, "", `${window.location.pathname}${window.location.search}`);
  }
}
